import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaFilePdf, FaFileWord } from 'react-icons/fa';
import { FiDownload as Download } from 'react-icons/fi';

const AuditDetails = ({ auditData }) => {
  const { id } = useParams();
  const audit = auditData.find((item) => item.id === parseInt(id, 10));
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);

  if (!audit) {
    return (
      <div className="min-h-screen p-6 bg-gray-100 flex justify-center items-center">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Audit Not Found</h2>
          <p className="text-gray-600">The requested audit could not be found.</p>
        </div>
      </div>
    );
  }

  const handleExport = (format) => {
    setIsDownloadDropdownOpen(false);
    let extension = '';
    if (format === 'pdf') extension = 'pdf';
    else if (format === 'excel') extension = 'xlsx'; // important fix here
    else if (format === 'word') extension = 'doc';
  
    const filename = `audit_${audit.id}.${extension}`;

    if (format === 'pdf') {
      const doc = new jsPDF();
      doc.text(`Audit Details - ID: ${audit.id}`, 10, 10);
      let y = 20;
      const addLine = (label, value) => {
        doc.text(`${label}: ${value}`, 10, y);
        y += 7;
      };

      addLine('Title', audit.title || 'N/A');
      addLine('ID', audit.id);
      addLine('Location', audit.location);
      addLine('Department', audit.department);
      addLine('Auditor', audit.auditor || 'N/A');
      addLine('Date', audit.date || 'N/A');
      addLine('Due Date', audit.dueDate);
      addLine('Type', audit.type || 'N/A');
      addLine('Status', audit.status);
      addLine('Priority', audit.priority || 'N/A');
      addLine('Progress', `${audit.progress}%`);
      addLine('Completion Rate', `${audit.completionRate || 'N/A'}%`);
      addLine('Risk', audit.risk);
      addLine('Findings', audit.findings || 'N/A');
      if (audit.description) {
        addLine('Description', audit.description);
      }

      if (audit.checklist && audit.checklist.length > 0) {
        doc.text('Checklist:', 10, y);
        y += 7;
        audit.checklist.forEach((item) => {
          doc.text(`- ${item.item}: ${item.completed ? 'Completed' : 'Pending'}`, 15, y);
          y += 5;
        });
      }

      doc.save(filename);
    } else if (format === 'excel') {
      const wb = XLSX.utils.book_new();
      const wsData = [
        ['Field', 'Value'],
        ['ID', audit.id],
        ['Title', audit.title || 'N/A'],
        ['Location', audit.location],
        ['Department', audit.department],
        ['Auditor', audit.auditor || 'N/A'],
        ['Date', audit.date || 'N/A'],
        ['Due Date', audit.dueDate],
        ['Type', audit.type || 'N/A'],
        ['Status', audit.status],
        ['Priority', audit.priority || 'N/A'],
        ['Progress', `${audit.progress}%`],
        ['Completion Rate', `${audit.completionRate || 'N/A'}%`],
        ['Risk', audit.risk],
        ['Findings', audit.findings || 'N/A'],
        ['Description', audit.description || 'N/A'],
        ['Checklist Item', 'Completed'],
        ...(audit.checklist || []).map((item) => [item.item, item.completed ? 'Yes' : 'No']),
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, 'Audit Details');
      XLSX.writeFile(wb, filename);
    } else if (format === 'word') {
      const content = `
        <h1>Audit Details - ID: ${audit.id}</h1>
        <p><strong>Title:</strong> ${audit.title || 'N/A'}</p>
        <p><strong>ID:</strong> ${audit.id}</p>
        <p><strong>Location:</strong> ${audit.location}</p>
        <p><strong>Department:</strong> ${audit.department}</p>
        <p><strong>Auditor:</strong> ${audit.auditor || 'N/A'}</p>
        <p><strong>Date:</strong> ${audit.date || 'N/A'}</p>
        <p><strong>Due Date:</strong> ${audit.dueDate}</p>
        <p><strong>Type:</strong> ${audit.type || 'N/A'}</p>
        <p><strong>Status:</strong> ${audit.status}</p>
        <p><strong>Priority:</strong> ${audit.priority || 'N/A'}</p>
        <p><strong>Progress:</strong> ${audit.progress}%</p>
        <p><strong>Completion Rate:</strong> ${audit.completionRate || 'N/A'}%</p>
        <p><strong>Risk:</strong> ${audit.risk}</p>
        <p><strong>Findings:</strong> ${audit.findings || 'N/A'}</p>
        ${audit.description ? `<p><strong>Description:</strong> ${audit.description}</p>` : ''}
        ${audit.checklist && audit.checklist.length > 0 ? '<h2>Checklist</h2><ul>' + audit.checklist.map(item => `<li><strong>${item.item}:</strong> ${item.completed ? 'Completed' : 'Pending'}</li>`).join('') + '</ul>' : ''}
      `;

      const blob = new Blob([content], { type: 'application/msword' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleDownloadDropdown = () => {
    setIsDownloadDropdownOpen((prev) => !prev);
  };

  return (
    <div className="min-h-screen p-6 bg-gray-100 relative">
      <div className="bg-white shadow rounded-lg p-8">
        <h2 className="text-3xl font-semibold text-blue-600 mb-6">Audit Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* ... (rest of your audit details rendering code) ... */}
          <div>
            <h3 className="text-xl font-semibold text-blue-600 mb-3">Basic Information</h3>
            <p className="mb-2"><span className="font-semibold text-gray-600">ID:</span> <span className="text-gray-800">{audit.id}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Title:</span> <span className="text-gray-800">{audit.title || 'N/A'}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Location:</span> <span className="text-gray-800">{audit.location}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Department:</span> <span className="text-gray-800">{audit.department}</span></p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-blue-600 mb-3">Details</h3>
            <p className="mb-2"><span className="font-semibold text-gray-600">Auditor:</span> <span className="text-gray-800">{audit.auditor || 'N/A'}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Date:</span> <span className="text-gray-800">{audit.date || 'N/A'}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Due Date:</span> <span className="text-gray-800">{audit.dueDate}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Type:</span> <span className="text-gray-800">{audit.type || 'N/A'}</span></p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-blue-600 mb-3">Status & Progress</h3>
            <p className="mb-2"><span className="font-semibold text-gray-600">Status:</span> <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                audit.status === 'Completed' ? 'bg-green-100 text-green-800' :
                audit.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                audit.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>{audit.status}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Priority:</span> <span className="text-gray-800">{audit.priority || 'N/A'}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Progress:</span> <span className="text-gray-800">{audit.progress}%</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Completion Rate:</span> <span className="text-gray-800">{audit.completionRate || 'N/A'}%</span></p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-blue-600 mb-3">Risk & Findings</h3>
            <p className="mb-2"><span className="font-semibold text-gray-600">Risk:</span> <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                audit.risk === 'Low' ? 'bg-green-100 text-green-800' :
                audit.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>{audit.risk}</span></p>
            <p className="mb-2"><span className="font-semibold text-gray-600">Findings:</span> <span className="text-gray-800">{audit.findings || 'N/A'}</span></p>
          </div>
        </div>

        {audit.description && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-blue-600 mb-3">Description</h3>
            <p className="text-gray-800">{audit.description}</p>
          </div>
        )}

        {audit.checklist && audit.checklist.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold text-blue-600 mb-3">Checklist</h3>
            <ul className="list-disc pl-6 text-gray-800">
              {audit.checklist.map((item) => (
                <li key={item.id} className="mb-2">
                  <span className="font-semibold">{item.item}:</span>{' '}
                  {item.completed ? <span className="text-green-500 font-semibold">Completed</span> : <span className="text-yellow-500 font-semibold">Pending</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Download Button */}
      <div className="absolute bottom-8 right-4 flex items-center space-x-2 text-sm download-dropdown">
        <button
          onClick={toggleDownloadDropdown}
          className="px-3 py-1 border rounded bg-black text-white hover:bg-gray-400 flex items-center"
        >
          <Download className="h-4 w-4 mr-1" />
          Download
        </button>

        {isDownloadDropdownOpen && (
          <div className="absolute bottom-full right-0 mb-1 bg-white border rounded-md shadow-md z-50 min-w-[120px]">
            <button
              onClick={() => handleExport('excel')}
              className="block px-4 py-2 w-full text-left hover:bg-gray-100 flex items-center"
            >
              <FaFileExcel className="h-4 w-4 mr-2 text-green-600" />
              Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="block px-4 py-2 w-full text-left hover:bg-gray-100 flex items-center"
            >
              <FaFilePdf className="h-4 w-4 mr-2 text-red-600" />
              PDF
            </button>
            <button
              onClick={() => handleExport('word')}
              className="block px-4 py-2 w-full text-left hover:bg-gray-100 flex items-center"
            >
              <FaFileWord className="h-4 w-4 mr-2 text-blue-600" />
              Word
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditDetails;